
import React, { useState } from 'react';
import { 
  ArrowLeft, 
  User, 
  CreditCard, 
  Shield, 
  Key, 
  Bell, 
  Check, 
  ExternalLink, 
  Save, 
  Plus, 
  Copy,
  Smartphone,
  AlertCircle
} from 'lucide-react';
import { SubscriptionTier } from '../../types';

interface SettingsViewProps {
  plan: SubscriptionTier;
  onBack: () => void;
}

type SettingsTab = 'profile' | 'subscription' | 'security' | 'api' | 'notifications';

export const SettingsView: React.FC<SettingsViewProps> = ({ plan, onBack }) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [copied, setCopied] = useState(false);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto animate-in slide-in-from-right duration-500">
      <div className="flex items-center space-x-4 mb-8">
        <button onClick={onBack} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-600">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-black text-slate-900">Account Settings</h1>
          <p className="text-sm text-slate-500">Manage your profile, organization, and service configurations.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Navigation Sidebar */}
        <div className="lg:col-span-1 space-y-1">
          <TabButton 
            active={activeTab === 'profile'} 
            onClick={() => setActiveTab('profile')} 
            icon={<User size={18} />} 
            label="Profile & Org" 
          />
          <TabButton 
            active={activeTab === 'subscription'} 
            onClick={() => setActiveTab('subscription')} 
            icon={<CreditCard size={18} />} 
            label="Billing & Plan" 
          />
          <TabButton 
            active={activeTab === 'security'} 
            onClick={() => setActiveTab('security')} 
            icon={<Shield size={18} />} 
            label="Security & Auth" 
          />
          <TabButton 
            active={activeTab === 'api'} 
            onClick={() => setActiveTab('api')} 
            icon={<Key size={18} />} 
            label="API & Integration" 
          />
          <TabButton 
            active={activeTab === 'notifications'} 
            onClick={() => setActiveTab('notifications')} 
            icon={<Bell size={18} />} 
            label="Notifications" 
          />
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3 space-y-6">
          {activeTab === 'profile' && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-in fade-in duration-300">
              <div className="p-6 border-b border-slate-100">
                <h3 className="text-lg font-bold text-slate-900">Personal Information</h3>
                <p className="text-sm text-slate-500">Update your account details and profile photo.</p>
              </div>
              <div className="p-6 space-y-6">
                <div className="flex items-center space-x-6">
                  <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Julian" className="w-20 h-20 rounded-2xl bg-slate-100 border border-slate-200" alt="Avatar" />
                  <button className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-lg hover:bg-slate-800">Change Photo</button>
                  <button className="px-4 py-2 border border-slate-200 text-slate-600 text-xs font-bold rounded-lg hover:bg-slate-50">Remove</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputGroup label="Full Name" placeholder="Julian Casablancas" />
                  <InputGroup label="Job Title" placeholder="Senior Compliance Officer" />
                  <InputGroup label="Work Email" placeholder="julian@minerva-regtech.com" disabled />
                  <InputGroup label="Company Name" placeholder="Minerva Financial S.A." />
                </div>
              </div>
              <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end">
                <button className="bg-blue-600 text-white px-6 py-2 rounded-xl font-bold text-sm flex items-center space-x-2 hover:bg-blue-700 shadow-lg shadow-blue-500/20">
                  <Save size={16} />
                  <span>Save Changes</span>
                </button>
              </div>
            </div>
          )}

          {activeTab === 'subscription' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="bg-slate-900 text-white p-8 rounded-3xl relative overflow-hidden shadow-2xl">
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div>
                    <span className="px-3 py-1 bg-blue-500 text-[10px] font-black uppercase rounded-full tracking-widest mb-4 inline-block">Current Plan</span>
                    <h2 className="text-3xl font-black mb-2">{plan} Intelligence</h2>
                    <p className="text-slate-400 text-sm max-w-md">Your plan includes advanced ESG scoring, Crystal API monitoring, and priority case resolution.</p>
                  </div>
                  <div className="text-center md:text-right">
                    <p className="text-4xl font-black mb-1">${plan === 'Pro' ? '249' : plan === 'Enterprise' ? '999' : '0'}</p>
                    <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Next Billing: Nov 27, 2023</p>
                    <button className="mt-6 px-8 py-3 bg-white text-slate-900 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-100 transition-all">
                      Upgrade Plan
                    </button>
                  </div>
                </div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600 rounded-full blur-[100px] opacity-20 translate-x-1/2 -translate-y-1/2"></div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                <div className="p-6 border-b border-slate-100">
                  <h3 className="text-lg font-bold text-slate-900">Payment Methods</h3>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 mb-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-8 bg-slate-900 rounded-md flex items-center justify-center text-white font-bold text-[10px]">VISA</div>
                      <div>
                        <p className="text-sm font-bold text-slate-900">•••• •••• •••• 4242</p>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Expires 12/25</p>
                      </div>
                    </div>
                    <button className="text-xs font-black text-rose-600 hover:underline">Remove</button>
                  </div>
                  <button className="w-full py-3 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center space-x-2 text-slate-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all">
                    <Plus size={16} />
                    <span className="text-xs font-bold uppercase tracking-widest">Add New Payment Method</span>
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100">
                  <h3 className="text-lg font-bold text-slate-900">Recent Invoices</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {[
                    { id: 'INV-2023-012', date: 'Oct 27, 2023', amount: '$249.00', status: 'Paid' },
                    { id: 'INV-2023-011', date: 'Sep 27, 2023', amount: '$249.00', status: 'Paid' },
                    { id: 'INV-2023-010', date: 'Aug 27, 2023', amount: '$249.00', status: 'Paid' },
                  ].map((inv, i) => (
                    <div key={i} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                      <div>
                        <p className="text-sm font-bold text-slate-900">{inv.id}</p>
                        <p className="text-xs text-slate-500">{inv.date}</p>
                      </div>
                      <div className="flex items-center space-x-6">
                        <span className="text-sm font-black text-slate-900">{inv.amount}</span>
                        <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-black uppercase rounded">{inv.status}</span>
                        <button className="text-slate-400 hover:text-slate-900"><ExternalLink size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-in fade-in duration-300">
              <div className="p-6 border-b border-slate-100">
                <h3 className="text-lg font-bold text-slate-900">Security Settings</h3>
                <p className="text-sm text-slate-500">Protect your compliance data with advanced security layers.</p>
              </div>
              <div className="p-6 space-y-8">
                <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-100 rounded-2xl">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-white rounded-xl shadow-sm text-blue-600"><Smartphone size={24} /></div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">Two-Factor Authentication (MFA)</p>
                      <p className="text-xs text-slate-500">Recommended: Authenticator app or SMS verification.</p>
                    </div>
                  </div>
                  <div className="w-12 h-6 bg-blue-600 rounded-full relative p-1 cursor-pointer">
                    <div className="w-4 h-4 bg-white rounded-full translate-x-6"></div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Update Password</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputGroup label="Current Password" type="password" placeholder="••••••••" />
                    <div></div>
                    <InputGroup label="New Password" type="password" placeholder="••••••••" />
                    <InputGroup label="Confirm New Password" type="password" placeholder="••••••••" />
                  </div>
                </div>

                <div className="pt-8 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-rose-600">Delete Account</p>
                      <p className="text-xs text-slate-500">Permanently remove all intelligence history and data. This cannot be undone.</p>
                    </div>
                    <button className="px-4 py-2 border border-rose-200 text-rose-600 text-xs font-bold rounded-lg hover:bg-rose-50 transition-colors">
                      Terminate Account
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'api' && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-in fade-in duration-300">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Developer API Keys</h3>
                  <p className="text-sm text-slate-500">Use these keys to integrate Minerva into your own platforms.</p>
                </div>
                <button className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-xl hover:bg-slate-800 flex items-center space-x-2">
                  <Plus size={16} />
                  <span>Generate New Key</span>
                </button>
              </div>
              <div className="p-6">
                <div className="space-y-6">
                   <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-[10px] font-black bg-blue-100 text-blue-700 px-2 py-0.5 rounded uppercase">Production</span>
                          <span className="text-sm font-bold text-slate-900">Stripe_Compliance_Live</span>
                        </div>
                        <div className="flex items-center space-x-2 text-xs font-mono text-slate-500">
                           <span>pk_live_51M4...9X4Z</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button onClick={() => handleCopy('pk_live_51M4XJ6XF9M1D3D612')} className="p-2 hover:bg-slate-200 rounded-lg transition-all text-slate-400 hover:text-slate-900 relative">
                          {copied ? <Check size={18} className="text-emerald-500" /> : <Copy size={18} />}
                        </button>
                        <button className="p-2 hover:bg-rose-50 rounded-lg transition-all text-slate-400 hover:text-rose-600">
                          <AlertCircle size={18} />
                        </button>
                      </div>
                   </div>

                   <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-between opacity-60">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-[10px] font-black bg-slate-200 text-slate-600 px-2 py-0.5 rounded uppercase">Sandbox</span>
                          <span className="text-sm font-bold text-slate-900">Web_Integration_Test</span>
                        </div>
                        <div className="flex items-center space-x-2 text-xs font-mono text-slate-500">
                           <span>sk_test_2138...D3D6</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="p-2 hover:bg-slate-200 rounded-lg transition-all text-slate-400">
                          <Copy size={18} />
                        </button>
                      </div>
                   </div>
                </div>

                <div className="mt-10 p-6 bg-slate-900 rounded-2xl text-white">
                  <h4 className="text-sm font-bold mb-4 flex items-center">
                    <Key size={16} className="mr-2 text-blue-400" /> API Documentation
                  </h4>
                  <p className="text-xs text-slate-400 mb-6 leading-relaxed">
                    Access our full OpenAPI specification to integrate Crystal API, Opoint, and ESPY intelligence directly into your workflow.
                  </p>
                  <button className="text-xs font-black text-blue-400 uppercase tracking-widest flex items-center hover:text-blue-300">
                    Explore Documentation <ExternalLink size={14} className="ml-2" />
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'notifications' && (
             <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-in fade-in duration-300">
              <div className="p-6 border-b border-slate-100">
                <h3 className="text-lg font-bold text-slate-900">Notification Channels</h3>
                <p className="text-sm text-slate-500">Choose how and when you want to be alerted of risk signals.</p>
              </div>
              <div className="p-6 space-y-6">
                <NotificationToggle 
                  title="AML Risk Alerts" 
                  desc="Critical on-ramp transaction flags from Crystal API." 
                  initialState={true} 
                />
                <NotificationToggle 
                  title="ESG Negative Media" 
                  desc="Alerts when an entity ESG score drops below your threshold." 
                  initialState={true} 
                />
                <NotificationToggle 
                  title="LEI Expiry Reminders" 
                  desc="Notices for 30, 7, and 1 day before expiration." 
                  initialState={true} 
                />
                <NotificationToggle 
                  title="Market Sentiment Shift" 
                  desc="Sudden negative social media spikes detected by ESPY." 
                  initialState={false} 
                />
                <NotificationToggle 
                  title="Daily Compliance Digest" 
                  desc="A summarized report of your intelligence dashboard." 
                  initialState={false} 
                />
              </div>
             </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TabButton: React.FC<{ active: boolean, onClick: () => void, icon: React.ReactNode, label: string }> = ({ active, onClick, icon, label }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
      active 
        ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' 
        : 'text-slate-500 hover:bg-white hover:text-slate-900'
    }`}
  >
    {icon}
    <span>{label}</span>
  </button>
);

const InputGroup: React.FC<{ label: string, placeholder?: string, type?: string, disabled?: boolean }> = ({ label, placeholder, type = 'text', disabled }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-black text-slate-500 uppercase tracking-widest">{label}</label>
    <input 
      type={type}
      disabled={disabled}
      placeholder={placeholder}
      className={`w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${disabled ? 'bg-slate-50 cursor-not-allowed text-slate-400' : ''}`} 
    />
  </div>
);

const NotificationToggle: React.FC<{ title: string, desc: string, initialState: boolean }> = ({ title, desc, initialState }) => {
  const [enabled, setEnabled] = useState(initialState);
  return (
    <div className="flex items-center justify-between py-2">
      <div className="max-w-md">
        <p className="text-sm font-bold text-slate-900">{title}</p>
        <p className="text-xs text-slate-500">{desc}</p>
      </div>
      <button 
        onClick={() => setEnabled(!enabled)}
        className={`w-12 h-6 rounded-full relative transition-all ${enabled ? 'bg-blue-600' : 'bg-slate-200'}`}
      >
        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${enabled ? 'left-7' : 'left-1'}`}></div>
      </button>
    </div>
  );
};
